import React, { useRef, useCallback, useReducer, useMemo, useEffect, forwardRef, useImperativeHandle } from 'react';
import { StyleSheet, View, Platform } from 'react-native';
import { useTheme } from '@/context/ThemeContext';
import { useTranslation } from '@/hooks/useTranslation';
import { feedbackApi } from '@/lib/api';
import Turnstile from '@/components/Turnstile';
import FloatingLabelTextarea from '@/components/Inputs/FloatingLabelTextarea';
import PropTypes from 'prop-types';
import { useToastStore } from '@/store';
import StatusContainer from '@/components/StatusContainer';

// Create styles outside component
const createStyles = () => StyleSheet.create({
  container: {
    width: '100%',
    gap: 16,
  },
});

// Platform check
const isWeb = Platform.OS === 'web';

// Form validation
const validateFeedback = (feedback, t) => {
  if (!feedback.trim()) {
    return t('feedback.form.feedbackRequired');
  }
  if (feedback.trim().length < 10) {
    return t('feedback.form.feedbackTooShort');
  }
  return null;
};

// Form reducer
const formReducer = (state, action) => {
  switch (action.type) {
    case 'SET_FEEDBACK':
      return { ...state, feedback: action.payload, error: '' };
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'RESET':
      return { ...initialState };
    default:
      return state;
  }
};

// Initial state
const initialState = {
  feedback: '',
  error: '',
  isLoading: false,
};

const FeedbackForm = forwardRef(({
  onClose,
  initialContent = '',
  conversationId,
  messageId,
  onValidityChange,
}, ref) => {
  const { t } = useTranslation();
  const { addToast } = useToastStore(state => state.actions);
  const turnstileRef = useRef(null);
  
  const [formState, dispatch] = useReducer(formReducer, {
    ...initialState,
    feedback: initialContent,
  });

  const isValid = !!formState.feedback.trim();

  // Notify the parent whenever validity changes so externally-rendered
  // submit buttons (e.g. the /feedback page) can enable/disable in sync
  // with what the user types. The imperative `isValid` below is a one-time
  // snapshot and cannot drive a live disabled state on its own.
  useEffect(() => {
    onValidityChange?.(isValid);
  }, [isValid, onValidityChange]);

  // Memoize styles
  const styles = useMemo(() => createStyles(), []);

  // Memoized handlers
  const handleFeedbackChange = useCallback((text) => {
    dispatch({ type: 'SET_FEEDBACK', payload: text });
  }, []);

  // Validate form
  const validateForm = useCallback(() => {
    const feedbackError = validateFeedback(formState.feedback, t);
    if (feedbackError) {
      dispatch({ type: 'SET_ERROR', payload: feedbackError });
      return false;
    }
    return true;
  }, [formState.feedback, t]);

  const handleSubmit = useCallback(async () => {
    try {
      if (!validateForm()) {
        return false;
      }

      dispatch({ type: 'SET_LOADING', payload: true });
      dispatch({ type: 'SET_ERROR', payload: '' });

      let turnstileToken = null;
      if (isWeb) {
        try {
          turnstileToken = await turnstileRef.current?.execute();
        } catch (captchaError) {
          dispatch({ type: 'SET_ERROR', payload: t('feedback.form.captchaRequired') });
          dispatch({ type: 'SET_LOADING', payload: false });
          return false;
        }
      }

      if (conversationId && messageId) {
        await feedbackApi.submitMessageFeedback(conversationId, messageId, formState.feedback.trim());
      } else {
        await feedbackApi.submit(formState.feedback.trim(), isWeb ? turnstileToken : undefined);
      }

      dispatch({ type: 'RESET' });
      
      addToast(t('feedback.form.success'), 'success');
      return true;
    } catch (error) {
      const errorMessage = error.message || t('feedback.form.error');
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      addToast(errorMessage, 'error');
      return false;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  }, [formState.feedback, t, addToast, validateForm, conversationId, messageId]);

  useImperativeHandle(ref, () => ({
    handleSubmit,
    isLoading: formState.isLoading,
    isValid,
  }));

  return (
    <View style={styles.container}>
      <FloatingLabelTextarea
        label={t('feedback.form.message.label')}
        value={formState.feedback}
        onChangeText={handleFeedbackChange}
        height={200}
        multiline
        textAlignVertical="top"
        editable={!formState.isLoading}
      />

      {formState.error ? (
        <StatusContainer type="error" message={formState.error} />
      ) : null}

      {isWeb && <Turnstile ref={turnstileRef} />}
    </View>
  );
});

FeedbackForm.propTypes = {
  onClose: PropTypes.func,
  initialContent: PropTypes.string,
  conversationId: PropTypes.string,
  messageId: PropTypes.string,
  onValidityChange: PropTypes.func,
};

export default React.memo(FeedbackForm); 